import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Compass, Users, BookOpen, Brain, Rocket,
  Heart, ShieldCheck, Star, MessageCircle, GraduationCap, MapPin,
  Check, X, CalendarDays, ShoppingBag,
} from "lucide-react";
const HERO_IMG = "https://images.unsplash.com/photo-1761416938064-b91b81262c84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxjaGlsZCUyMHJlYWRpbmclMjBvdXRkb29ycyUyMHdhcm0lMjBsaWdodHxlbnwwfHx8fDE3ODM0NTkxNjZ8MA&ixlib=rb-4.1.0&q=85";
const PARENT_IMG = "https://images.unsplash.com/photo-1758598738092-a7cd486baadd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxwYXJlbnQlMjBhbmQlMjBjaGlsZCUyMHN0dWR5aW5nJTIwdG9nZXRoZXIlMjBsYXB0b3B8ZW58MHx8fHwxNzgzNDU5MTY3fDA&ixlib=rb-4.1.0&q=85";

const testimonials = [
  { name: "Anjali Sharma", city: "Bengaluru", quote: "The 15-minute assessment gave me more clarity than months of research. We chose hybrid learning and my daughter thrives." },
  { name: "Ravi Menon", city: "Kochi", quote: "The AI Assistant answered every doubt about NIOS in plain English. Finally, someone treats parents like adults, not customers." },
  { name: "Priyanka Gupta", city: "Delhi", quote: "The Career Navigator opened our eyes. We're now designing my son's learning path around biotech — his real passion." },
];

const faqs = [
  { q: "Is homeschooling legal in India?", a: "Yes. India recognises homeschooling. Certification is typically through NIOS for Class 10 and 12, which is accepted by most universities." },
  { q: "When should we start planning?", a: "The earlier the better — even at age 2, understanding your child's temperament helps. Our assessment is designed for children aged 2 to 18." },
  { q: "How much does homeschooling cost?", a: "Anywhere from ₹3,000 to ₹40,000 per month depending on curriculum, tutors, and materials. Our report estimates this for your specific plan." },
  { q: "Do we need to be teachers?", a: "No. You facilitate learning. Most families use a mix of curricula, online resources, tutors, mentors, and community classes." },
  { q: "Will my child socialise enough?", a: "Yes, if you plan for it. Communities, sports, arts, and co-ops give homeschooled kids diverse peer interaction — often more than school." },
];

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Ambient orbs for warmth */}
        <div aria-hidden className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-brand-green/10 blur-3xl" />
        <div aria-hidden className="absolute -top-10 right-0 w-72 h-72 rounded-full bg-brand-orange/10 blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span data-testid="hero-badge" className="chip mb-5">
              <Sparkles size={12} /> AI-powered · Warmly human · Unbiased
            </span>
            <h1 data-testid="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-brand-blue">
              Is homeschooling right for <span className="text-brand-green">your child</span>?
            </h1>
            <p data-testid="hero-subtext" className="mt-6 text-lg text-brand-muted leading-relaxed max-w-xl">
              Take a 15-minute assessment. Receive an unbiased, personalized report on the best learning journey for your child — traditional school, homeschooling, hybrid, or something else entirely.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/assessment" data-testid="hero-start-assessment" className="btn-primary">
                Start assessment <ArrowRight size={16} />
              </Link>
              <Link to="/parent-readiness" data-testid="hero-parent-readiness" className="btn-secondary">
                Check <em>your</em> readiness →
              </Link>
              <Link to="/assistant" data-testid="hero-ai-assistant" className="btn-ghost">
                <MessageCircle size={16} /> Talk to AI
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-5 text-sm text-brand-muted">
              <div className="flex -space-x-2">
                {["A", "R", "P", "K"].map((c, i) => (
                  <span key={i} className={`w-8 h-8 rounded-full ring-2 ring-white flex items-center justify-center text-xs font-semibold text-white ${["bg-brand-blue","bg-brand-green","bg-brand-orange","bg-brand-blue-hover"][i]}`}>{c}</span>
                ))}
              </div>
              <span><strong className="text-brand-blue">2,400+ parents</strong> found their child's path this month</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(15,41,66,0.35)] aspect-[4/5]">
              <img src={HERO_IMG} alt="Child learning outdoors" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 surface p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-green-soft flex items-center justify-center text-brand-green"><Brain size={20} /></div>
                <div>
                  <div className="text-xs text-brand-muted">Personalized report</div>
                  <div className="font-heading font-semibold text-brand-blue">Suitability score: 82/100</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* BRAND BAR — rotating value props */}
        <div className="relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-10">
            <div className="rounded-3xl bg-brand-blue text-white p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 md:gap-10 shadow-[0_20px_50px_-20px_rgba(15,41,66,0.45)] overflow-hidden relative">
              <div aria-hidden className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-brand-orange/20 blur-2xl" />
              <div className="flex items-center gap-3 shrink-0 relative">
                <div className="w-11 h-11 rounded-2xl bg-brand-orange text-white flex items-center justify-center"><Sparkles size={20} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/60">The FuturePath promise</div>
                  <div className="font-heading font-semibold text-white text-lg">Every child. Every path. One home.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 flex-1 text-sm relative">
                {[
                  ["Unbiased", "No paid rankings"],
                  ["Age 2 → 18", "Grows with your child"],
                  ["Warm AI", "Powered by Claude 4.5"],
                  ["Gives back", "15% funds orphanages"],
                ].map(([t, s]) => (
                  <div key={t}>
                    <div className="font-heading font-semibold text-brand-orange">{t}</div>
                    <div className="text-white/75 text-xs">{s}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="border-y border-brand-border bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              ["15 min", "Assessment"],
              ["2 → 18", "Ages served"],
              ["18+", "Curricula covered"],
              ["100%", "Unbiased guidance"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-heading text-3xl font-bold text-brand-blue">{n}</div>
                <div className="text-sm text-brand-muted mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARENT READINESS SPOTLIGHT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20">
        <Link
          to="/parent-readiness"
          data-testid="landing-readiness-cta"
          className="block rounded-3xl bg-gradient-to-br from-brand-orange to-orange-600 text-white p-8 lg:p-12 relative overflow-hidden group transition-shadow hover:shadow-[0_30px_80px_-25px_rgba(245,138,76,0.5)]"
        >
          <div aria-hidden className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="grid md:grid-cols-12 gap-6 items-center relative">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/15 text-white mb-4">
                <Compass size={12} /> Parent Preparedness Checkpoint
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold leading-tight">
                Before you plan for your child — check if <em>you</em> are ready.
              </h2>
              <p className="mt-3 text-white/85 text-base leading-relaxed max-w-2xl">
                A warm 10-question self-check on your time, patience, support system, and mindset. You'll get a readiness
                score and honest, practical next steps — because parenting the journey matters as much as picking the curriculum.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <span className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-brand-orange font-medium transition-transform group-hover:scale-105">
                Start the check <ArrowRight size={16} />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* WHY PARENTS CHOOSE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          <div className="overline mb-3">Why parents come to us</div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-brand-blue leading-tight">
            One question. Every parent asks it.
            <br />
            <span className="text-brand-muted font-normal">"What's the best learning journey for my child?"</span>
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6 lg:gap-8">
          {[
            { icon: Compass, title: "Unbiased guidance", body: "We never sell you homeschooling — or school. We help you see clearly." },
            { icon: Brain, title: "AI-personalized", body: "Reports built for your child's age, interests, personality, and family goals." },
            { icon: Users, title: "Human community", body: "Real parents, tutors and mentors across India and internationally." },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="surface surface-hover p-7"
            >
              <div className="w-12 h-12 rounded-2xl bg-brand-green-soft text-brand-green flex items-center justify-center mb-5">
                <f.icon size={22} />
              </div>
              <h3 className="text-xl font-semibold text-brand-blue">{f.title}</h3>
              <p className="mt-2 text-brand-muted leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-white border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline mb-3">Traditional vs Homeschool</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-brand-blue leading-tight">Two paths. Neither is wrong.</h2>
            <p className="mt-4 text-brand-muted leading-relaxed">
              Both offer real benefits and real trade-offs. Our job is to help you weigh them against what your child and family actually need.
            </p>
            <Link to="/assessment" className="btn-secondary mt-8">Get my personalized comparison</Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            <div className="surface p-6">
              <div className="chip-blue mb-3"><GraduationCap size={12} /> Traditional</div>
              <ul className="space-y-2.5 text-sm">
                {["Structured peer environment","Established credentialing","Standardised curriculum","Less parental time"].map((x) => (
                  <li key={x} className="flex gap-2"><Check size={16} className="text-brand-green shrink-0 mt-0.5" /> <span>{x}</span></li>
                ))}
                {["Less personalised","Fixed pace"].map((x) => (
                  <li key={x} className="flex gap-2"><X size={16} className="text-brand-orange shrink-0 mt-0.5" /> <span>{x}</span></li>
                ))}
              </ul>
            </div>
            <div className="surface p-6 border-brand-green/40">
              <div className="chip mb-3"><Heart size={12} /> Homeschool</div>
              <ul className="space-y-2.5 text-sm">
                {["Fully personalised pace","Deep interest exploration","Family bonding","Flexible curriculum"].map((x) => (
                  <li key={x} className="flex gap-2"><Check size={16} className="text-brand-green shrink-0 mt-0.5" /> <span>{x}</span></li>
                ))}
                {["Requires parent time","Requires social planning"].map((x) => (
                  <li key={x} className="flex gap-2"><X size={16} className="text-brand-orange shrink-0 mt-0.5" /> <span>{x}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE — Everything, in one place */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="overline mb-3">Explore FuturePath</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-brand-blue leading-tight">
              Everything a parent needs — in one warm home.
            </h2>
            <p className="mt-3 text-lg text-brand-muted">
              Pick any door below. Every section is designed to be useful in under two minutes.
            </p>
          </div>
          <Link to="/assessment" className="btn-primary shrink-0">
            Start with assessment <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid md:grid-cols-6 gap-4 lg:gap-5">
          {/* Assessment — hero card */}
          <Link
            to="/assessment"
            data-testid="explore-assessment"
            className="md:col-span-3 md:row-span-2 rounded-3xl p-8 lg:p-10 group relative overflow-hidden bg-gradient-to-br from-brand-blue to-brand-blue-hover text-white transition-all hover:shadow-[0_25px_60px_-15px_rgba(15,41,66,0.5)]"
          >
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-brand-orange/20 text-brand-orange mb-5"><Sparkles size={12} /> Start here</div>
            <h3 className="text-3xl lg:text-4xl font-semibold leading-tight">The 15-minute parent assessment.</h3>
            <p className="mt-4 text-white/80 leading-relaxed max-w-md">
              Answer 18 warm questions. Get an AI-personalized report with a suitability score, weekly schedule,
              curriculum picks, and career pathways — free forever.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-brand-orange font-medium">
              Start free <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </div>
            <div aria-hidden className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-brand-green/10 blur-2xl" />
          </Link>

          {/* Curriculum */}
          <Link to="/curriculum" data-testid="explore-curriculum" className="md:col-span-3 surface surface-hover p-7 group">
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-xl bg-brand-green-soft text-brand-green flex items-center justify-center"><BookOpen size={20} /></div>
              <span className="chip">18+ curricula</span>
            </div>
            <h3 className="text-xl font-semibold text-brand-blue">Curriculum Explorer</h3>
            <p className="mt-1.5 text-sm text-brand-muted">NCERT, CBSE, ICSE, IB, Cambridge, Montessori, Waldorf, Unschooling & more — pros, cons, cost.</p>
            <div className="mt-4 flex items-center text-brand-green font-medium text-sm">Compare curricula <ArrowRight size={14} className="ml-1 transition-transform group-hover:translate-x-1" /></div>
          </Link>

          {/* Activities */}
          <Link to="/activities" data-testid="explore-activities" className="md:col-span-3 surface surface-hover p-7 group">
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-xl bg-brand-orange-soft text-brand-orange flex items-center justify-center"><CalendarDays size={20} /></div>
              <span className="chip-orange">6 age bands</span>
            </div>
            <h3 className="text-xl font-semibold text-brand-blue">Age-based Activities</h3>
            <p className="mt-1.5 text-sm text-brand-muted">Daily routines and weekly schedules that fit your child's stage — from 2 to 18 years.</p>
            <div className="mt-4 flex items-center text-brand-orange font-medium text-sm">See routines <ArrowRight size={14} className="ml-1 transition-transform group-hover:translate-x-1" /></div>
          </Link>

          {/* Shop */}
          <Link to="/marketplace" data-testid="explore-marketplace" className="md:col-span-2 surface surface-hover p-6 group">
            <div className="w-11 h-11 rounded-xl bg-brand-bg text-brand-blue border border-brand-border flex items-center justify-center mb-3"><ShoppingBag size={20} /></div>
            <h3 className="font-semibold text-brand-blue">Shop</h3>
            <p className="mt-1 text-xs text-brand-muted">Curated books, kits & tools. 15% gives back.</p>
          </Link>

          {/* Careers */}
          <Link to="/careers" data-testid="explore-careers" className="md:col-span-2 surface surface-hover p-6 group">
            <div className="w-11 h-11 rounded-xl bg-brand-green-soft text-brand-green flex items-center justify-center mb-3"><Rocket size={20} /></div>
            <h3 className="font-semibold text-brand-blue">Future Careers</h3>
            <p className="mt-1 text-xs text-brand-muted">Careers that matter in 2045 + timeless human skills.</p>
          </Link>

          {/* AI Assistant */}
          <Link to="/assistant" data-testid="explore-assistant" className="md:col-span-2 surface surface-hover p-6 group">
            <div className="w-11 h-11 rounded-xl bg-brand-orange-soft text-brand-orange flex items-center justify-center mb-3"><MessageCircle size={20} /></div>
            <h3 className="font-semibold text-brand-blue">AI Assistant</h3>
            <p className="mt-1 text-xs text-brand-muted">Ask any parenting question — warm expert answer.</p>
          </Link>

          {/* Communities — extra emphasis */}
          <Link
            to="/communities"
            data-testid="explore-communities"
            className="md:col-span-3 rounded-3xl p-7 group relative overflow-hidden bg-gradient-to-br from-brand-green to-emerald-700 text-white transition-all hover:shadow-[0_25px_60px_-15px_rgba(5,150,105,0.4)]"
          >
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/15 text-white mb-4"><Users size={12} /> Join a tribe</div>
            <h3 className="text-2xl font-semibold">Communities in your city.</h3>
            <p className="mt-2 text-white/85 text-sm max-w-sm">Bengaluru, Mumbai, Delhi, Chennai, Pune, Kochi and beyond — plus online circles for every curriculum.</p>
            <div className="mt-5 inline-flex items-center gap-2 text-white font-medium text-sm">Find your community <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></div>
          </Link>

          {/* Tutors */}
          <Link to="/tutors" data-testid="explore-tutors" className="md:col-span-3 surface surface-hover p-7 group">
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-brand-blue flex items-center justify-center"><GraduationCap size={20} /></div>
              <span className="chip-blue">Verified</span>
            </div>
            <h3 className="text-xl font-semibold text-brand-blue">Tutors & Mentors</h3>
            <p className="mt-1.5 text-sm text-brand-muted">Subject tutors and industry mentors — scientists, founders, doctors, designers — hand-picked for warmth and skill.</p>
            <div className="mt-4 flex items-center text-brand-blue font-medium text-sm">Browse experts <ArrowRight size={14} className="ml-1 transition-transform group-hover:translate-x-1" /></div>
          </Link>

          {/* Learning for All */}
          <Link
            to="/impact"
            data-testid="explore-impact"
            className="md:col-span-6 rounded-3xl p-7 lg:p-8 group relative overflow-hidden bg-brand-orange-soft border border-brand-orange/30 transition-all hover:shadow-[0_20px_40px_-15px_rgba(245,138,76,0.35)]"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-brand-orange text-white flex items-center justify-center shrink-0"><Heart size={24} /></div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-brand-orange font-bold mb-1">Learning for All</div>
                <h3 className="text-2xl font-semibold text-brand-blue leading-snug">
                  15% of every commission funds learning for orphaned children.
                </h3>
                <p className="mt-1 text-sm text-brand-blue/70">See how our marketplace helps bridge the education inequality gap →</p>
              </div>
              <div className="shrink-0">
                <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-orange text-white font-medium text-sm transition-transform group-hover:scale-105">
                  See impact <ArrowRight size={14} />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img src={PARENT_IMG} alt="Parent and child learning" className="rounded-3xl w-full h-[520px] object-cover" />
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-3">How it works</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-brand-blue leading-tight">Three steps to clarity.</h2>
            <div className="mt-10 space-y-6">
              {[
                { n: "01", t: "Take the assessment", d: "Answer 18 warm, thoughtful questions about your child and family. Takes 15 minutes." },
                { n: "02", t: "Read your personalized report", d: "AI writes a report for YOUR child — suitability score, pros, cons, curriculum, weekly schedule, and roadmap." },
                { n: "03", t: "Design the journey", d: "Explore curricula, join communities, book tutors, meet mentors — and update the plan as your child grows." },
              ].map((s) => (
                <div key={s.n} className="flex gap-6">
                  <div className="w-12 h-12 rounded-2xl bg-brand-orange-soft text-brand-orange font-heading font-bold flex items-center justify-center shrink-0">{s.n}</div>
                  <div>
                    <h4 className="text-xl font-semibold text-brand-blue">{s.t}</h4>
                    <p className="mt-1 text-brand-muted leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          <div className="overline mb-3">Parents like you</div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-brand-blue leading-tight">Real families. Real clarity.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <div key={t.name} className="surface p-7">
              <div className="flex text-brand-orange mb-4">{[0,0,0,0,0].map((_,i) => <Star key={i} size={16} fill="currentColor" />)}</div>
              <p className="text-brand-blue leading-relaxed">"{t.quote}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-blue text-white flex items-center justify-center font-semibold">{t.name[0]}</div>
                <div>
                  <div className="font-semibold text-brand-blue text-sm">{t.name}</div>
                  <div className="text-xs text-brand-muted flex items-center gap-1"><MapPin size={12} /> {t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-brand-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-center mb-12">
            <div className="overline mb-3">FAQ</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-brand-blue">Questions parents ask us</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} data-testid={`faq-${i}`} className="surface p-5 group">
                <summary className="cursor-pointer font-heading font-semibold text-brand-blue text-lg list-none flex justify-between items-center">
                  {f.q}
                  <span className="text-brand-green group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-brand-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="rounded-3xl p-10 lg:p-16 text-center bg-gradient-to-br from-brand-blue to-brand-blue-hover text-white shadow-[0_30px_80px_-30px_rgba(15,41,66,0.5)]">
          <ShieldCheck size={36} className="mx-auto text-brand-orange mb-5" />
          <h2 className="text-3xl sm:text-4xl font-semibold leading-tight">Give your child a future built around them.</h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">Start with a free assessment. No signup required. Warm, unbiased, actionable in 15 minutes.</p>
          <Link to="/assessment" data-testid="final-cta-btn" className="btn-primary mt-8">
            Take the free assessment <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
